import { FC } from 'react';
import { DocumentIcon } from '@heroicons/react/outline';
import { AssessmentQuestionType } from '../../../../generated/graphql';
import { ProviderAssessmentsWithoutScheduleQuery } from '../../../../generated/graphql';
import {
  isFreeTextAssessmentQuestion,
  isMultiSelectAssessmentQuestion,
  isMultipleChoiceAssessmentQuestion,
  isMultipleChoiceScoredAssessmentQuestion,
  isRichTextAssessmentQuestion,
  isSignatureAssessmentQuestion,
  isMultiSignatureAssessmentQuestion,
  isStatementAssessmentQuestion,
  isInitialsAssessmentQuestion,
  isFileUploadAssessmentQuestion,
  isAutofillAssessmentQuestion,
  isDatePickerAssessmentQuestion,
  isPatientContactAssessmentQuestion,
  isProviderSignatureAssessmentQuestion,
} from '../../../lib/assessments';
import SlideoverSection from '../../SlideoverSection';
import QuestionItem from '../QuestionItem';
import { formatContactTypeLabel } from '../../../lib/patientContactLabels';

export enum QuestionSubscale {
  MultipleChoice = 'Multiple choice',
  MultiSelect = 'Multiple select',
  FreeText = 'Short text',
  RichText = 'Long text',
  Initials = 'Client initials',
  Signature = 'Client signature',
  MultiSignature = 'Collaborative signature',
  Statement = 'Statement (read-only)',
  FileUpload = 'File upload',
  Autofill = 'Autofill',
  DatePicker = 'Date Picker',
  PatientContact = 'Client Contact',
  ProviderSignature = 'Practitioner signature',
}

const QuestionsSection: FC<{
  questions?:
    | ProviderAssessmentsWithoutScheduleQuery['assessments'][number]['questions']
    | ProviderAssessmentsWithoutScheduleQuery['forms'][number]['questions'];
  questionFrame?: string | null;
  title?: string;
  isActivityForm?: boolean;
  /** When true, numbered list is shown even for custom forms (library preview). */
  showQuestionNumbers?: boolean;
}> = ({
  questions,
  questionFrame,
  title = 'Questions',
  isActivityForm = false,
  showQuestionNumbers = false,
}) => {
  const hideQuestionIndex = isActivityForm && !showQuestionNumbers;
  const hasFileUploadQuestions = questions?.some(
    (question) => question.questionType === AssessmentQuestionType.FileUpload,
  );

  return (
    <SlideoverSection
      title={title}
      TitleRightComponent={
        hasFileUploadQuestions ? (
          <DocumentIcon className="h-4 w-4 text-neutral-125" />
        ) : undefined
      }
    >
      {questionFrame && (
        <div className="mb-4 font-sans text-caption text-neutral-125">
          {questionFrame}
        </div>
      )}
      {questions?.map((question, index) => {
        if (isMultipleChoiceScoredAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.MultipleChoice}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (
          isMultipleChoiceAssessmentQuestion(question) ||
          isMultiSelectAssessmentQuestion(question)
        ) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={
                isMultiSelectAssessmentQuestion(question)
                  ? QuestionSubscale.MultiSelect
                  : QuestionSubscale.MultipleChoice
              }
              answerOptions={question.answerOptions?.map(
                (answerOption) => answerOption.answerOption,
              )}
              isForm={hideQuestionIndex}
              questionType={question.questionType}
            />
          );
        }
        if (isFreeTextAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.FreeText}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isRichTextAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.RichText}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isInitialsAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.Initials}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isSignatureAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.Signature}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isProviderSignatureAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.ProviderSignature}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isMultiSignatureAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.MultiSignature}
              assessmentSignatureUsers={question.assessmentSignatureUsers}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isStatementAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.Statement}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isFileUploadAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.FileUpload}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isAutofillAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.Autofill}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isDatePickerAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              subscale={QuestionSubscale.DatePicker}
              isForm={hideQuestionIndex}
            />
          );
        }
        if (isPatientContactAssessmentQuestion(question)) {
          return (
            <QuestionItem
              key={index}
              questionIndex={index}
              question={question.question}
              caption={
                [
                  question.contactType
                    ? formatContactTypeLabel(question.contactType)
                    : null,
                  'Name, email, and phone number',
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
              subscale={QuestionSubscale.PatientContact}
              isForm={hideQuestionIndex}
            />
          );
        }
        return null;
      })}
    </SlideoverSection>
  );
};

export default QuestionsSection;
